
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, KeyRound } from 'lucide-react';
import { PasswordChangeForm } from '@/components/auth/PasswordChangeForm';

const RecruiterDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const userProfile = {
    name: "Jane Smith",
    agency: "Tech Talent Specialists",
    location: "Sydney, Australia",
  };

  const myReviews = [
    {
      id: "rev1",
      company: "Canva",
      lastPlacementDate: new Date('2025-03-01'),
      ratings: { company: 4.5, culture: 4, process: 3.5 }
    },
    {
      id: "rev2",
      company: "Atlassian",
      lastPlacementDate: new Date('2025-02-15'),
      ratings: { company: 5, culture: 4.5, process: 4 }
    }
  ];

  if (!user) {
    navigate('/recruiter-login');
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-24 px-6 pb-16">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-3xl font-bold text-redflags-dark mb-6">Recruiter Dashboard</h1>
          
          <Tabs defaultValue="profile" className="space-y-6">
            <TabsList>
              <TabsTrigger value="profile">My Profile</TabsTrigger>
              <TabsTrigger value="reviews">My Reviews</TabsTrigger>
              <TabsTrigger value="placements">Placements</TabsTrigger>
              <TabsTrigger value="settings">Settings</TabsTrigger>
            </TabsList>
            
            <TabsContent value="profile" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Profile Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-redflags-gray">Name</p>
                    <p className="font-medium">{userProfile.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-redflags-gray">Agency</p>
                    <p className="font-medium">{userProfile.agency}</p>
                  </div>
                  <div>
                    <p className="text-sm text-redflags-gray">Location</p>
                    <p className="font-medium">{userProfile.location}</p>
                  </div>
                  <div>
                    <p className="text-sm text-redflags-gray">Email</p>
                    <p className="font-medium">{user.email}</p>
                  </div>
                  <Button className="mt-4">Edit Profile</Button>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="reviews" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>My Company Reviews</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {myReviews.map(review => (
                      <div key={review.id} className="p-4 border rounded-md">
                        <div className="flex justify-between mb-2">
                          <h3 className="font-medium">{review.company}</h3>
                          <Button size="sm" variant="outline">Edit Review</Button>
                        </div>
                        <div className="text-sm space-y-1">
                          <div className="flex justify-between">
                            <span>Company Rating:</span>
                            <span>{review.ratings.company}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Culture Rating:</span>
                            <span>{review.ratings.culture}/5</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Process Rating:</span>
                            <span>{review.ratings.process}/5</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button className="w-full mt-4">Add New Review</Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="placements" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>My Placements</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {myReviews.map(review => (
                      <div key={review.id} className="p-4 border rounded-md">
                        <div className="flex justify-between mb-2">
                          <h3 className="font-medium">{review.company}</h3>
                          <Button size="sm" variant="outline">Update</Button>
                        </div>
                        <div className="text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>Last Placement: {review.lastPlacementDate.toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button className="w-full mt-4">Add New Placement</Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="settings" className="space-y-6">
              <Card>
                <CardHeader>
                  <div className="flex items-center gap-2">
                    <KeyRound className="w-5 h-5 text-redflags-red" />
                    <CardTitle>Change Password</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <PasswordChangeForm />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      
      {/* Standardized footer wrapper for consistent styling */}
      <div className="w-full">
        <Footer />
      </div>
    </div>
  );
};

export default RecruiterDashboard;
